import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle2, Columns2 } from 'lucide-react';
import { shotUrl, type Shot } from '../../api.js';
import { Badge } from '../../ui/kit.js';
import { useIsDesktop } from '../../ui/useIsDesktop.js';

/* The screenshot viewer, rebuilt (V3 §5). One component serves the job
   detail and the review screen: a uniform grid, and a true full-screen
   viewer with swipe, arrow keys and click zones, pinch and double-tap
   zoom, a route and width badge always visible, a filmstrip on a desk and
   real dot buttons on a phone, and a before/after compare that appears
   ONLY when a true baseline exists and says what it is comparing against. */

export interface BaselineMeta {
  exists: boolean;
  mergeSha?: string | null;
  jobTitle?: string | null;
  recordedAt?: string | null;
}

export function sortShots(shots: Shot[]): Shot[] {
  // Route groups first, phone width before desk, stable by capture time.
  const routeKey = (s: Shot) => s.route ?? `~${s.file}`;
  return [...shots].sort(
    (a, b) =>
      routeKey(a).localeCompare(routeKey(b)) ||
      (a.width ?? 0) - (b.width ?? 0) ||
      a.created_at.localeCompare(b.created_at),
  );
}

/* The viewer's own shape, from the width the shot was taken at. The gallery
   tiles are deliberately uniform instead — see `.ops-shotcell--tile`. */
function aspectFor(width: number | null): string {
  if (width && width > 900) return '16 / 10';
  if (width) return '9 / 16';
  return '4 / 3';
}

export interface ShotGroup {
  route: string;
  count: number;
  failed: number;
  firstAt: string;
}

/* How many thumbnails a screen shows before it says "and N more". Four is
   two rows on a phone: enough to see what a screen looks like, few enough
   that ten screens is a page a person can actually reach the bottom of.
   Measured on the 417-shot fixture: six per group is about 6,000px of
   scroll, four is about 3,500, and the old flat grid of every shot at its
   true 9/16 was nearer sixty thousand. */
const PER_GROUP = 4;

/* The gallery (V6 section H). It used to be one flat grid of every shot a
   job had ever taken, which for the job that opened this round meant 417
   images, 417 filmstrip cells and 417 dots on one page. Now it is what a
   gallery is: grouped by the screen it photographed, the groups the layout
   lint objected to first, six thumbnails a group with the rest one tap
   away, and the whole set still reachable. */
export function ShotGrid(props: {
  jobId: string;
  repo: string;
  shots: Shot[];
  groups?: ShotGroup[];
  total?: number;
  testId?: string;
  onNeedMore?: () => void;
  loadingMore?: boolean;
}) {
  const shots = useMemo(() => sortShots(props.shots), [props.shots]);
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  /* The groups the server named, in the order it named them, filled with
     the shots this page actually holds. A group the page has nothing for
     yet still shows its count, so the shape of the whole set is honest
     before every byte of it has arrived. */
  const grouped = useMemo(() => {
    const byRoute = new Map<string, Shot[]>();
    for (const s of shots) {
      const key = s.route ?? '(unrouted)';
      const list = byRoute.get(key);
      if (list) list.push(s);
      else byRoute.set(key, [s]);
    }
    const declared = props.groups?.length
      ? props.groups
      : [...byRoute.entries()]
          .map(([route, list]) => ({
            route,
            count: list.length,
            failed: list.filter((s) => s.verdict?.verdict === 'fail').length,
            firstAt: list[0]?.created_at ?? '',
          }))
          .sort((a, b) => (b.failed ? 1 : 0) - (a.failed ? 1 : 0) || b.count - a.count || a.route.localeCompare(b.route));
    return declared.map((g) => ({ ...g, shots: byRoute.get(g.route) ?? [] }));
  }, [shots, props.groups]);

  const total = props.total ?? shots.length;
  const missing = Math.max(0, total - shots.length);

  return (
    <>
      <div className="ops-gallery" data-testid={props.testId ?? 'shot-grid'} data-total={total}>
        <p className="ops-gallery__lede" data-testid="gallery-lede">
          {total} {total === 1 ? 'screenshot' : 'screenshots'} across {grouped.length}{' '}
          {grouped.length === 1 ? 'screen' : 'screens'}
          {grouped.some((g) => g.failed) ? ', the ones the layout lint objected to first' : ''}.
        </p>
        {grouped.map((g) => {
          const open = expanded[g.route];
          const shown = open ? g.shots : g.shots.slice(0, PER_GROUP);
          const rest = g.count - shown.length;
          return (
            <section className="ops-gallery__group" key={g.route} data-route={g.route}>
              <h4 className="ops-gallery__grouphead">
                <span className="ops-gallery__route">{g.route}</span>
                <span className="ops-gallery__count">
                  {g.failed ? <b className="ops-gallery__failed">{g.failed} flagged</b> : null}
                  {g.failed ? ' · ' : ''}
                  {g.count}
                </span>
              </h4>
              <div className="ops-shotgrid">
                {shown.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    /* Uniform, and short. A phone-shaped thumbnail at its
                       true 9/16 is 285px tall on a 380px screen: six of
                       them a group across ten groups is eight thousand
                       pixels of scroll, which is the page nobody can reach
                       the bottom of wearing a different hat (V6 section H).
                       The caption carries the width; the viewer carries the
                       true shape. */
                    className={`ops-shotcell ops-shotcell--tile${s.verdict?.verdict === 'fail' ? ' ops-shotcell--bad' : ''}`}
                    onClick={() => setOpenIdx(shots.indexOf(s))}
                  >
                    <img src={shotUrl(props.jobId, s)} alt={s.route ?? s.file} loading="lazy" />
                    <span className="ops-shotcell__cap">
                      {s.verdict ? (s.verdict.verdict === 'fail' ? '✗ ' : '✓ ') : ''}
                      {s.width ? `${s.width}px` : s.file}
                      {s.source === 'ci' ? ' · CI' : ''}
                    </span>
                  </button>
                ))}
              </div>
              {rest > 0 ? (
                <button
                  type="button"
                  className="ops-gallery__more"
                  data-testid={`gallery-more-${g.route.replace(/[^a-z0-9]+/gi, '-')}`}
                  onClick={() => {
                    if (g.shots.length < g.count) props.onNeedMore?.();
                    setExpanded((e) => ({ ...e, [g.route]: true }));
                  }}
                >
                  {rest} more on this screen
                </button>
              ) : null}
            </section>
          );
        })}
        {missing > 0 ? (
          <button
            type="button"
            className="ops-gallery__more ops-gallery__more--all"
            data-testid="gallery-load-more"
            disabled={props.loadingMore}
            onClick={() => props.onNeedMore?.()}
          >
            {props.loadingMore ? 'Fetching…' : `Fetch the other ${missing}`}
          </button>
        ) : null}
      </div>
      {openIdx !== null ? (
        <ShotViewer
          jobId={props.jobId}
          repo={props.repo}
          shots={shots}
          total={total}
          index={openIdx}
          onIndexChange={(i) => {
            setOpenIdx(i);
            // Walking towards the end of what is loaded fetches the next
            // page, so swiping never runs into an invisible wall.
            if (i >= shots.length - 3 && shots.length < total) props.onNeedMore?.();
          }}
          onClose={() => setOpenIdx(null)}
        />
      ) : null}
    </>
  );
}

export function ShotViewer(props: {
  jobId: string;
  repo: string;
  shots: Shot[];
  /** How many the job has, which is not how many this page has loaded. */
  total?: number;
  index: number;
  onIndexChange: (i: number) => void;
  onClose: () => void;
}) {
  const { shots, index } = props;
  const shot = shots[index];
  const desktop = useIsDesktop();
  const [compare, setCompare] = useState(false);
  const [baseline, setBaseline] = useState<BaselineMeta | null>(null);
  const [showFindings, setShowFindings] = useState(false);
  const [imgState, setImgState] = useState<'loading' | 'ready' | 'error'>('loading');

  /* Zoom and swipe live in refs; only the transform state renders. */
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
  const [dragX, setDragX] = useState(0);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{
    mode: 'none' | 'swipe' | 'pan' | 'pinch';
    startX: number;
    startY: number;
    startT: { scale: number; x: number; y: number };
    startDist: number;
    lastTap: number;
  }>({ mode: 'none', startX: 0, startY: 0, startT: { scale: 1, x: 0, y: 0 }, startDist: 0, lastTap: 0 });

  const go = useCallback(
    (delta: number) => {
      const next = index + delta;
      if (next < 0 || next >= shots.length) return;
      setTransform({ scale: 1, x: 0, y: 0 });
      setDragX(0);
      setCompare(false);
      setShowFindings(false);
      setImgState('loading');
      props.onIndexChange(next);
    },
    [index, shots.length, props],
  );

  /* Body scroll locked while open; Escape closes; arrows navigate. */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') props.onClose();
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [go, props]);

  /* The compare offer: only when a TRUE before exists — a metadata probe,
     not a full image download. */
  useEffect(() => {
    setBaseline(null);
    setCompare(false);
    if (!shot?.route || !shot.width) return;
    let alive = true;
    fetch(`/api/baselines?repo=${encodeURIComponent(props.repo)}&route=${encodeURIComponent(shot.route)}&width=${shot.width}&meta=1`)
      .then(async (r) => {
        if (!alive) return;
        if (r.ok) setBaseline({ exists: true, ...((await r.json()) as Omit<BaselineMeta, 'exists'>) });
        else setBaseline({ exists: false });
      })
      .catch(() => alive && setBaseline({ exists: false }));
    return () => {
      alive = false;
    };
  }, [shot?.route, shot?.width, props.repo]);

  /* Preload the neighbors so a swipe lands on pixels, not a spinner. */
  useEffect(() => {
    for (const n of [index - 1, index + 1]) {
      const s = shots[n];
      if (s) new Image().src = shotUrl(props.jobId, s);
    }
  }, [index, shots, props.jobId]);

  const dist = () => {
    const pts = [...pointers.current.values()];
    return pts.length < 2 ? 0 : Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (compare) return;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (pointers.current.size === 2) {
      g.mode = 'pinch';
      g.startDist = dist();
      g.startT = transform;
      return;
    }
    g.startX = e.clientX;
    g.startY = e.clientY;
    g.startT = transform;
    g.mode = transform.scale > 1 ? 'pan' : 'swipe';
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (g.mode === 'pinch' && pointers.current.size === 2) {
      const scale = Math.min(5, Math.max(1, (g.startT.scale * dist()) / Math.max(g.startDist, 1)));
      setTransform((t) => ({ ...t, scale }));
      return;
    }
    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;
    if (g.mode === 'pan') {
      setTransform({ ...g.startT, x: g.startT.x + dx, y: g.startT.y + dy });
    } else if (g.mode === 'swipe') {
      setDragX(dx);
      void dy;
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    const g = gesture.current;
    if (g.mode === 'pinch') {
      if (pointers.current.size < 2) {
        g.mode = 'none';
        setTransform((t) => (t.scale <= 1.05 ? { scale: 1, x: 0, y: 0 } : t));
      }
      return;
    }
    if (g.mode === 'swipe') {
      const dx = e.clientX - g.startX;
      const dy = e.clientY - g.startY;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
        go(dx < 0 ? 1 : -1);
      } else if (Math.abs(dx) < 8 && Math.abs(dy) < 8) {
        // A tap. Two of them inside 300ms toggle zoom at the tap point.
        const nowMs = Date.now();
        if (nowMs - g.lastTap < 300) {
          setTransform((t) => (t.scale > 1 ? { scale: 1, x: 0, y: 0 } : { scale: 2.5, x: 0, y: 0 }));
          g.lastTap = 0;
        } else {
          g.lastTap = nowMs;
        }
      }
      setDragX(0);
    }
    g.mode = 'none';
  };

  if (!shot) return null;
  const findings = shot.verdict?.findings ?? [];
  const beforeLabel = baseline?.exists
    ? `last merged look${baseline.mergeSha ? ` · ${String(baseline.mergeSha).slice(0, 8)}` : ''}`
    : '';

  /* The strip and the dots show a window around where you are, never the
     whole set: a job with 417 shots used to put 417 cells and 417 dots
     under the picture (V6 section H). Swipe, arrows and keys still walk
     every one of them, and the position counter says where you are. */
  const STRIP = 12;
  const from = Math.max(0, Math.min(index - Math.floor(STRIP / 2), shots.length - STRIP));
  const windowShots = shots.slice(from, from + STRIP);

  return (
    <div className="ops-shotviewer" role="dialog" aria-label="Screenshot viewer" data-testid="shot-viewer">
      <header className="ops-shotviewer__bar">
        <div className="ops-shotviewer__meta">
          <b>{shot.route ?? shot.file}</b>
          <span>
            {shot.width ? `${shot.width}px` : ''}
            {shot.source === 'ci' ? ' · CI' : ' · session'}
            {` · ${index + 1}/${shots.length}`}
          </span>
        </div>
        {findings.length ? (
          <button
            type="button"
            className="ops-shotviewer__chip"
            aria-expanded={showFindings}
            onClick={() => setShowFindings((v) => !v)}
          >
            {shot.verdict?.verdict === 'fail' ? <AlertTriangle size={13} /> : <CheckCircle2 size={13} />}
            {findings.length} finding{findings.length === 1 ? '' : 's'}
          </button>
        ) : null}
        {baseline?.exists ? (
          <button
            type="button"
            className={`ops-shotviewer__chip${compare ? ' is-on' : ''}`}
            data-testid="compare-toggle"
            onClick={() => {
              setCompare((v) => !v);
              setTransform({ scale: 1, x: 0, y: 0 });
            }}
          >
            <Columns2 size={13} /> {compare ? 'Just this shot' : 'Compare with before'}
          </button>
        ) : null}
        <button type="button" className="ops-shotviewer__close" aria-label="Close" onClick={props.onClose}>
          <X size={18} />
        </button>
      </header>

      {compare && shot.route && shot.width ? (
        <CompareStage
          beforeSrc={`/api/baselines?repo=${encodeURIComponent(props.repo)}&route=${encodeURIComponent(shot.route)}&width=${shot.width}`}
          afterSrc={shotUrl(props.jobId, shot)}
          beforeLabel={beforeLabel}
        />
      ) : (
        <div
          className="ops-shotviewer__stage"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {desktop && index > 0 ? (
            <button type="button" className="ops-shotviewer__zone ops-shotviewer__zone--left" aria-label="Previous" onClick={() => go(-1)}>
              <ChevronLeft size={22} />
            </button>
          ) : null}
          {imgState === 'loading' ? <div className="ops-shotviewer__skeleton" /> : null}
          {imgState === 'error' ? (
            <div className="ops-shotviewer__error">
              <p>This screenshot could not be loaded.</p>
              <button type="button" className="ops-linklike" onClick={() => setImgState('loading')}>
                Try again
              </button>
            </div>
          ) : (
            <img
              key={shot.id}
              className="ops-shotviewer__img"
              src={shotUrl(props.jobId, shot)}
              alt={shot.route ?? shot.file}
              draggable={false}
              onLoad={() => setImgState('ready')}
              onError={() => setImgState('error')}
              style={{
                transform: `translate(${transform.x + dragX}px, ${transform.y}px) scale(${transform.scale})`,
                transition: gesture.current.mode === 'none' && dragX === 0 ? 'transform 0.18s ease' : 'none',
                opacity: imgState === 'ready' ? 1 : 0,
              }}
            />
          )}
          {desktop && index < shots.length - 1 ? (
            <button type="button" className="ops-shotviewer__zone ops-shotviewer__zone--right" aria-label="Next" onClick={() => go(1)}>
              <ChevronRight size={22} />
            </button>
          ) : null}
        </div>
      )}

      {showFindings && findings.length ? (
        <div className="ops-shotviewer__findings">
          {findings.map((f, i) => (
            <p key={i}>
              <Badge tone={f.severity === 'warning' ? 'accent' : 'bad'}>{f.rule}</Badge> {f.element ?? ''}
              {f.message ? ` ${f.message}` : ''}
            </p>
          ))}
        </div>
      ) : null}

      <footer className="ops-shotviewer__foot">
        {/* A window around where you are, never the whole set. A job with
            four hundred shots used to render four hundred filmstrip cells
            and four hundred dots under the picture (V6 section H); the
            arrows, swipe and keys still walk the whole set. */}
        {/* The count is the JOB's, not the page's. Saying "13 of 120" on a
            job that took 417 tells a person they have seen the lot when
            they have seen a page (V6 section H). */}
        <span className="ops-shotviewer__pos" data-testid="viewer-position">
          {index + 1} of {props.total ?? shots.length}
        </span>
        {desktop ? (
          <div className="ops-shotviewer__strip">
            {windowShots.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`ops-shotviewer__thumb${shots.indexOf(s) === index ? ' is-on' : ''}`}
                aria-label={`Shot ${shots.indexOf(s) + 1}`}
                onClick={() => {
                  const i = shots.indexOf(s);
                  if (i !== index) {
                    setImgState('loading');
                    setTransform({ scale: 1, x: 0, y: 0 });
                    setCompare(false);
                    props.onIndexChange(i);
                  }
                }}
              >
                <img src={shotUrl(props.jobId, s)} alt="" loading="lazy" />
              </button>
            ))}
          </div>
        ) : (
          <div className="ops-shotviewer__dots" role="tablist">
            {windowShots.map((s) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={shots.indexOf(s) === index}
                aria-label={`Shot ${shots.indexOf(s) + 1}`}
                className={`ops-shotviewer__dot${shots.indexOf(s) === index ? ' is-on' : ''}`}
                onClick={() => {
                  const i = shots.indexOf(s);
                  if (i !== index) {
                    setImgState('loading');
                    setTransform({ scale: 1, x: 0, y: 0 });
                    setCompare(false);
                    props.onIndexChange(i);
                  }
                }}
              >
                <span />
              </button>
            ))}
          </div>
        )}
      </footer>
    </div>
  );
}

/* Before/after with a VISIBLE, pointer-tracked handle. Left of the divider
   is the before (the last merged look); right of it is this job's shot —
   the tags sit on their own sides. */
function CompareStage(props: { beforeSrc: string; afterSrc: string; beforeLabel: string }) {
  const [pct, setPct] = useState(50);
  const stage = useRef<HTMLDivElement | null>(null);
  const dragging = useRef(false);

  const setFromClientX = (clientX: number) => {
    const rect = stage.current?.getBoundingClientRect();
    if (!rect) return;
    setPct(Math.min(98, Math.max(2, ((clientX - rect.left) / rect.width) * 100)));
  };

  return (
    <div
      ref={stage}
      className="ops-cmp"
      data-testid="compare-stage"
      onPointerDown={(e) => {
        dragging.current = true;
        (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
        setFromClientX(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && setFromClientX(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
      onPointerCancel={() => (dragging.current = false)}
    >
      <div className="ops-cmp__frame">
        <img className="ops-cmp__img" src={props.beforeSrc} alt="Before" draggable={false} />
        <img
          className="ops-cmp__img ops-cmp__img--after"
          src={props.afterSrc}
          alt="After"
          draggable={false}
          style={{ clipPath: `inset(0 0 0 ${pct}%)` }}
        />
        <div className="ops-cmp__divider" style={{ left: `${pct}%` }}>
          <span className="ops-cmp__grip" />
        </div>
        <span className="ops-cmp__tag ops-cmp__tag--before">before · {props.beforeLabel}</span>
        <span className="ops-cmp__tag ops-cmp__tag--after">after · this job</span>
      </div>
    </div>
  );
}
